// LogsManager.jsx - Activity logs viewer with advanced filtering
import React, { useState, useEffect, useCallback } from 'react';
import { 
  ScrollText, 
  Trash2, 
  RefreshCw, 
  Loader2, 
  User, 
  Settings, 
  FileText,
  Users,
  ChevronLeft,
  ChevronRight,
  Filter,
  Search,
  Calendar,
  X
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LogsManager = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [categories, setCategories] = useState([]);
  const [uniqueUsers, setUniqueUsers] = useState([]);
  const [purging, setPurging] = useState(false);
  const [showConfirmPurge, setShowConfirmPurge] = useState(false);
  
  // Filters
  const [category, setCategory] = useState('all');
  const [selectedUser, setSelectedUser] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  const LOGS_PER_PAGE = 50;

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchLogs();
  }, [page, category, selectedUser, debouncedSearch, dateFrom, dateTo]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: LOGS_PER_PAGE.toString(),
        skip: (page * LOGS_PER_PAGE).toString()
      });
      if (category !== 'all') params.append('category', category);
      if (selectedUser !== 'all') params.append('user_id', selectedUser);
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const response = await fetch(`${BACKEND_URL}/api/admin/logs?${params}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
        setTotal(data.total || 0);
        if (data.users) setUniqueUsers(data.users);
      }
    } catch (err) {
      console.error('Error fetching logs:', err);
      toast.error('Erreur lors du chargement des logs');
    } finally {
      setLoading(false);
    }
  }, [page, category, selectedUser, debouncedSearch, dateFrom, dateTo]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/logs/categories`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const handlePurge = async () => {
    setPurging(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/logs`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setPage(0);
        fetchLogs();
      } else {
        toast.error('Erreur lors de la purge');
      }
    } catch (err) {
      console.error('Error purging logs:', err);
      toast.error('Erreur de connexion');
    } finally {
      setPurging(false);
      setShowConfirmPurge(false);
    }
  };

  const clearFilters = () => {
    setCategory('all');
    setSelectedUser('all');
    setSearchQuery('');
    setDateFrom('');
    setDateTo('');
    setPage(0);
  };

  const hasActiveFilters = category !== 'all' || selectedUser !== 'all' || searchQuery || dateFrom || dateTo;

  const getCategoryIcon = (cat) => {
    switch (cat) {
      case 'users': return <Users className="w-4 h-4" />;
      case 'system': return <Settings className="w-4 h-4" />;
      case 'content': return <FileText className="w-4 h-4" />;
      default: return <ScrollText className="w-4 h-4" />;
    }
  };

  const getCategoryLabel = (cat) => {
    switch (cat) {
      case 'users': return 'Utilisateurs';
      case 'system': return 'Système';
      case 'content': return 'Contenu';
      case 'general': return 'Général';
      default: return cat;
    }
  };

  const getCategoryColor = (cat) => {
    switch (cat) {
      case 'users': return 'bg-purple-100 text-purple-700';
      case 'system': return 'bg-blue-100 text-blue-700';
      case 'content': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getTabLabel = (tab) => {
    const tabLabels = {
      'dashboard': 'Tableau de Bord',
      'analytics': 'Statistiques',
      'page-home': 'Page Accueil',
      'page-equipment': 'Équipements',
      'page-services': 'Services',
      'page-partners': 'Partenaires',
      'page-references': 'Références',
      'page-events': 'Événements',
      'page-contact': 'Contact',
      'branding': 'Logos & Favicon',
      'page-images': 'Images Pages',
      'footer': 'Pied de Page',
      'social': 'Réseaux Sociaux',
      'reviews': 'Avis Google',
      'chat': 'Chat Tawk.to',
      'recaptcha': 'reCAPTCHA',
      'messages': 'Messages',
      'feedbacks': 'Demandes Partenariat',
      'notifications': 'Emails',
      'users': 'Utilisateurs',
      'logs': 'Journal',
      'maintenance': 'Maintenance',
      'backup': 'Sauvegarde',
      'system': 'Système'
    };
    return tabLabels[tab] || tab || '-';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const totalPages = Math.ceil(total / LOGS_PER_PAGE);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-light text-slate-800 flex items-center gap-2">
            <ScrollText className="w-6 h-6" />
            Journal d'activité
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {total} logs enregistrés (max 1000)
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => { setPage(0); fetchLogs(); }}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          
          {!showConfirmPurge ? (
            <Button 
              variant="destructive" 
              size="sm"
              onClick={() => setShowConfirmPurge(true)}
              disabled={total === 0}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Purger tout
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-sm text-red-600 font-medium">Confirmer ?</span>
              <Button 
                variant="destructive" 
                size="sm"
                onClick={handlePurge}
                disabled={purging}
              >
                {purging ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Oui'}
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowConfirmPurge(false)}
                disabled={purging}
              >
                Non
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search input */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
              placeholder="Rechercher dans les logs (action, détails, utilisateur...)"
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* Toggle filters */}
          <Button
            variant={showFilters || hasActiveFilters ? "default" : "outline"}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2"
          >
            <Filter className="w-4 h-4" />
            Filtres
            {hasActiveFilters && (
              <span className="bg-white text-blue-600 text-xs px-1.5 py-0.5 rounded-full">
                {[category !== 'all', selectedUser !== 'all', dateFrom, dateTo].filter(Boolean).length}
              </span>
            )}
          </Button>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Category filter */}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Catégorie</label>
              <select
                value={category}
                onChange={(e) => { setCategory(e.target.value); setPage(0); }}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Toutes les catégories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{getCategoryLabel(cat)}</option>
                ))}
              </select>
            </div>
            
            {/* User filter */}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Utilisateur</label>
              <select
                value={selectedUser}
                onChange={(e) => { setSelectedUser(e.target.value); setPage(0); }}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Tous les utilisateurs</option>
                {uniqueUsers.map(u => (
                  <option key={u.user_id} value={u.user_id}>
                    {u.user_name || u.user_email || u.user_id}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Date from */}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Date début</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
                  className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            {/* Date to */}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Date fin</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
                  className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* Clear filters button */}
        {hasActiveFilters && (
          <div className="mt-4 flex justify-end">
            <button
              onClick={clearFilters}
              className="text-sm text-blue-600 hover:underline flex items-center gap-1"
            >
              <X className="w-4 h-4" />
              Effacer tous les filtres
            </button>
          </div>
        )}
      </div>

      {/* Logs list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <ScrollText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p>{hasActiveFilters ? 'Aucun log correspondant aux filtres' : 'Aucun log enregistré'}</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Date/Heure</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Utilisateur</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Action</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Détails</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Onglet</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Catégorie</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {logs.map((log, idx) => (
                  <tr key={idx} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
                      {formatDate(log.timestamp)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {log.user_picture ? (
                          <img 
                            src={log.user_picture} 
                            alt="" 
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-slate-500" />
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium text-slate-800">{log.user_name || 'N/A'}</p>
                          <p className="text-xs text-slate-500">{log.user_email || ''}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-slate-800">
                      {log.action}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate" title={log.details}>
                      {log.details || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {getTabLabel(log.tab)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(log.category)}`}>
                        {getCategoryIcon(log.category)}
                        {getCategoryLabel(log.category)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
              <p className="text-sm text-slate-600">
                Page {page + 1} sur {totalPages} ({total} logs)
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LogsManager;
